import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL ;

class AuthApi {
  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: { "Content-Type": "application/json" },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token");
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  async login(data) {
    try {
      const response = await this.api.post("/auth/dashboard/login", {
        email: data.email,
        password: data.password,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Login failed",
      );
    }
  }

  // ── Register (customer) ───────────────────────────────────────────────────
  // POST /auth/register → { accessToken, user }
  // Returns same shape as login so callers can store token + redirect.
  async register(data) {
    try {
      const response = await this.api.post("/auth/register", {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        ...(data.phone ? { phone: data.phone } : {}),
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Registration failed",
      );
    }
  }

  // ── Send email verification code ─────────────────────────────────────────
  // POST /auth/send-verification → { message }
  async sendVerificationCode(email) {
    try {
      const response = await this.api.post("/auth/send-verification", { email });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to send verification code",
      );
    }
  }

  // ── Verify email with code ────────────────────────────────────────────────
  // POST /auth/verify-email → { verified: true }
  async verifyEmail(email, code) {
    try {
      const response = await this.api.post("/auth/verify-email", { email, code });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Invalid or expired verification code",
      );
    }
  }

  // ── Guest: send verification code (no auth required) ─────────────────────
  async sendGuestVerificationCode(email) {
    try {
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { "Content-Type": "application/json" },
      });
      const response = await api.post("/auth/send-verification", { email });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to send verification code",
      );
    }
  }

  // ── Guest: verify email code (no auth required) ───────────────────────────
  async verifyGuestEmail(email, code) {
    try {
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { "Content-Type": "application/json" },
      });
      const response = await api.post("/auth/verify-email", { email, code });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Invalid or expired verification code",
      );
    }
  }

  // ── Two-factor authentication ─────────────────────────────────────────────
  async verifyTwoFactor(mfaToken, code) {
    try {
      const response = await this.api.post("/auth/2fa/verify", {
        mfaToken,
        code,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Invalid or expired 2FA code",
      );
    }
  }

  async twoFactorStatus() {
    const response = await this.api.get("/auth/2fa/status");
    return response.data;
  }

  async twoFactorSetup() {
    const response = await this.api.post("/auth/2fa/setup");
    return response.data;
  }

  async twoFactorEnable(code) {
    try {
      const response = await this.api.post("/auth/2fa/enable", { code });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Invalid authenticator code",
      );
    }
  }

  async twoFactorDisable() {
    const response = await this.api.post("/auth/2fa/disable");
    return response.data;
  }

  logout() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
  }
}

export const authApi = new AuthApi();
